import { createContext, useContext, useState, type ReactNode } from 'react'

interface InflationContextType {
  showRealReturns: boolean
  setShowRealReturns: (value: boolean) => void
  inflationRate: number
  setInflationRate: (rate: number) => void
  calculateRealValue: (nominalValue: number, years: number) => number
  calculateRealReturn: (nominalReturn: number) => number
}

const InflationContext = createContext<InflationContextType | null>(null)

export function useInflation() {
  const context = useContext(InflationContext)
  if (!context) {
    throw new Error('useInflation must be used within an InflationProvider')
  }
  return context
}

interface InflationProviderProps {
  children: ReactNode
}

export function InflationProvider({ children }: InflationProviderProps) {
  const [showRealReturns, setShowRealReturns] = useState(false)
  const [inflationRate, setInflationRate] = useState(3) // Default 3% inflation

  const calculateRealValue = (nominalValue: number, years: number): number => {
    if (!showRealReturns) return nominalValue
    const inflationFactor = Math.pow(1 + inflationRate / 100, years)
    return nominalValue / inflationFactor
  }

  const calculateRealReturn = (nominalReturn: number): number => {
    // Fisher equation: real return ≈ nominal return - inflation
    // More accurate: (1 + nominal) / (1 + inflation) - 1
    const nominalDecimal = nominalReturn / 100
    const inflationDecimal = inflationRate / 100
    const realDecimal = (1 + nominalDecimal) / (1 + inflationDecimal) - 1
    return realDecimal * 100
  }

  const value: InflationContextType = {
    showRealReturns,
    setShowRealReturns,
    inflationRate,
    setInflationRate,
    calculateRealValue,
    calculateRealReturn,
  }

  return (
    <InflationContext.Provider value={value}>
      {children}
    </InflationContext.Provider>
  )
}
